import { untrack } from "svelte";
import { setSheetDisplayNone, sheetScope, unsetSheetDisplayNone } from "../utils/props";
import type { useSheetStack } from "./stack.svelte";
import { stack } from "../stack/global.svelte";
import { dispatchSheetEvent } from "../event/event.dispatch";
import {
  SHEET_EVENT_AFTER_CLOSE,
  SHEET_EVENT_AFTER_OPEN,
  SHEET_EVENT_CLOSE,
  SHEET_EVENT_FLYIN,
  SHEET_EVENT_FLYOUT,
  SHEET_EVENT_OPEN,
} from "../event/event.consts";

type InstanceState = ReturnType<typeof useSheetStack>;

const log = /*           */ sheetScope.scope("visibility");

const SHEET_OPEN_CLOSE_ANIMATION_DURATION = 210;

export const teleportSheetToBody = (host: HTMLOcSheetV1Element, placeholder: Comment): void => {
  /*                                                                    */
  if (import.meta.env.STORYBOOK) return;

  /*                                                     */
  if (host.parentNode === document.body) return;

  log.info("Teleporting sheet to body", host.id);
  host.parentNode?.replaceChild(placeholder, host);
  document.body.appendChild(host);
};

export const teleportSheetBack = (host: HTMLOcSheetV1Element, placeholder: Comment): void => {
  /*                                                                    */
  if (import.meta.env.STORYBOOK) return;

  /*                                                        */
  if (!placeholder.parentNode) return;

  log.info("Teleporting sheet back to original position", host.id);
  placeholder.parentNode.replaceChild(host, placeholder);
};

export const shouldBeginShow = (instanceState: InstanceState): boolean =>
  instanceState.isCurrent && instanceState.isActive;

export const shouldCompleteClose = (instanceState: InstanceState): boolean =>
  !instanceState.isCurrent && !instanceState.isActive;

export const shouldCompleteShow = (instanceState: InstanceState): boolean =>
  instanceState.isCurrent &&
  instanceState.isActive &&
  instanceState.isVisible &&
  !instanceState.isShown;

export const shouldBeginClose = (instanceState: InstanceState): boolean =>
  !instanceState.isCurrent &&
  instanceState.isActive &&
  instanceState.isVisible &&
  !instanceState.isShown;

export const shouldDispatchFlyIn = (instanceState: InstanceState): boolean =>
  instanceState.isCurrent && instanceState.isActive && instanceState.isVisible;

export const shouldDispatchFlyOut = (instanceState: InstanceState): boolean =>
  !instanceState.isCurrent && instanceState.isActive && !instanceState.isVisible;

export const withContentChange = (
  callback: () => void,
  timeout = SHEET_OPEN_CLOSE_ANIMATION_DURATION,
): number | undefined => {
  if (untrack(() => stack.contentChange)) return void callback();
  return window.setTimeout(callback, timeout);
};

/**
 *
 *
 *
 *
 *
 *
 *
 */
export const useSheetVisibility = (
  host: HTMLOcSheetV1Element,
  instanceState: InstanceState,
): void => {
  /**
 *
 *
 */
  const placeholder = document.createComment(host.id);

  /**
 *
 *
 */
  let animationTimeout: number | undefined;

  /*                                    */
  $effect(() => {
    const action = untrack(() => [...stack.action] as const);
    const reason = untrack(() => stack.reason);

    if (shouldBeginShow(instanceState)) {
      clearTimeout(animationTimeout);

      /*                            */
      teleportSheetToBody(host, placeholder);
      unsetSheetDisplayNone(host);

      /*                                */
      dispatchSheetEvent(SHEET_EVENT_OPEN, host, { reason, action: action[0] });

      withContentChange(() => {
        instanceState.isVisible = true;
      }, 0);
    }

    if (shouldCompleteClose(instanceState)) {
      /*                         */
      setSheetDisplayNone(host);
      teleportSheetBack(host, placeholder);

      dispatchSheetEvent(SHEET_EVENT_AFTER_CLOSE, host, {
        reason,
        action: action[0],
        closeType: action[1],
      });
    }
  });

  /*                                                 */
  $effect(() => {
    const action = untrack(() => [...stack.action] as const);
    const reason = untrack(() => stack.reason);

    if (shouldDispatchFlyIn(instanceState)) {
      dispatchSheetEvent(SHEET_EVENT_FLYIN, host, { reason, action: action[0] });
    }

    if (shouldDispatchFlyOut(instanceState)) {
      dispatchSheetEvent(SHEET_EVENT_FLYOUT, host, {
        reason,
        action: action[0],
        closeType: action[1],
      });
    }
  });

  $effect(() => {
    const action = untrack(() => [...stack.action] as const);
    const reason = untrack(() => stack.reason);

    if (shouldCompleteShow(instanceState)) {
      animationTimeout = withContentChange(() => {
        instanceState.isShown = true;

        dispatchSheetEvent(SHEET_EVENT_AFTER_OPEN, host, { reason, action: action[0] });
      });
    }

    if (shouldBeginClose(instanceState)) {
      clearTimeout(animationTimeout);

      instanceState.isShown = false;
      instanceState.isVisible = false;

      animationTimeout = withContentChange(() => {
        /*                                   */
        dispatchSheetEvent(SHEET_EVENT_CLOSE, host, {
          reason,
          action: action[0],
          closeType: action[1],
        });

        instanceState.isActive = false;
      });
    }
  });
};
